import type { Platform, PlatformKind } from "@kopi/platform";

export interface BackNavigationHistory {
  canGoBack(): boolean;
  back(): void;
}

/**
 * The topmost overlay consumes Back before route history; only an unobstructed root may exit.
 */
export function handleNativeBack(
  history: BackNavigationHistory,
  dismissOverlay: () => boolean = () => false,
): boolean {
  if (dismissOverlay()) return true;
  if (!history.canGoBack()) return false;
  history.back();
  return true;
}

/** Reuse Radix/vaul's Escape behavior, including dialogs that deliberately prevent dismissal. */
export function dismissActiveOverlay(): boolean {
  const overlay = document.querySelector(
    '[data-state="open"]:is([role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"])',
  );
  if (!overlay) return false;
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
  // Even when dismissal is prevented (e.g. while saving), never navigate underneath the overlay.
  return true;
}

/** Register native navigation before opening the Android workspace; failures must reach startup UI. */
export async function initializeNativeBack(
  kind: PlatformKind,
  create: () => Promise<Platform>,
  history: BackNavigationHistory,
  dismissOverlay: () => boolean = () => false,
): Promise<void> {
  if (kind !== "tauri-android") return;
  const runtime = await create();
  await runtime.onBackRequested?.(() => handleNativeBack(history, dismissOverlay));
}
